import React from "react";
import { AbsoluteFill, useCurrentFrame, interpolate, spring, useVideoConfig } from "remotion";

// 效果 51: Logo 揭示 - 渐变展开
export const LogoRevealGradient: React.FC<{
  logoText: string;
  color1: string;
  color2: string;
}> = ({ logoText, color1 = "#4ecdc4", color2 = "#ff6b6b" }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  
  const progress = spring({
    frame,
    fps,
    config: { damping: 20, stiffness: 100 },
  });
  
  const clipPath = `inset(0 ${100 - progress * 100}% 0 0)`;
  
  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#1a1a2e",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div
        style={{
          fontSize: 120,
          fontWeight: "bold",
          background: `linear-gradient(135deg, ${color1}, ${color2})`,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          clipPath,
        }}
      >
        {logoText}
      </div>
    </AbsoluteFill>
  );
};

// 效果 52: Logo 揭示 - 缩放弹跳
export const LogoRevealScale: React.FC<{ logoText: string; color: string }> = ({
  logoText,
  color = "#ffffff",
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  
  const scale = spring({
    frame,
    fps,
    config: { damping: 10, stiffness: 200, mass: 0.5 },
  });
  
  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#0f0f23",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div
        style={{
          fontSize: 120,
          fontWeight: "bold",
          color,
          transform: `scale(${scale})`,
          textShadow: `0 0 40px ${color}`,
        }}
      >
        {logoText}
      </div>
    </AbsoluteFill>
  );
};

// 效果 53: Logo 揭示 - 打字机 + 光标
export const LogoRevealTypewriter: React.FC<{
  logoText: string;
  color: string;
}> = ({ logoText, color = "#4ecdc4" }) => {
  const frame = useCurrentFrame();
  const charsToShow = Math.floor(interpolate(frame, [0, 60], [0, logoText.length]));
  
  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#000",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div style={{ fontSize: 100, fontFamily: "monospace", color }}>
        {logoText.slice(0, charsToShow)}
        <span
          style={{
            opacity: Math.sin(frame * 0.5) > 0 ? 1 : 0,
            marginLeft: 2,
          }}
        >
          |
        </span>
      </div>
    </AbsoluteFill>
  );
};

// 效果 54: Logo 揭示 - 遮罩划入
export const LogoRevealMask: React.FC<{
  logoText: string;
  color: string;
}> = ({ logoText, color = "#ffffff" }) => {
  const frame = useCurrentFrame();
  const progress = interpolate(frame, [0, 60], [0, 100], {
    extrapolateRight: "clamp",
  });
  
  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#1a1a2e",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div
        style={{
          fontSize: 120,
          fontWeight: "bold",
          color,
          clipPath: `circle(${progress}% at 50% 50%)`,
        }}
      >
        {logoText}
      </div>
    </AbsoluteFill>
  );
};

// 效果 55: Logo 揭示 - 分割重组
export const LogoRevealSplit: React.FC<{
  logoText: string;
  color: string;
}> = ({ logoText, color = "#ffffff" }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  
  const progress = spring({
    frame,
    fps,
    config: { damping: 15, stiffness: 100 },
  });
  
  const topY = interpolate(progress, [0, 1], [-100, 0]);
  const bottomY = interpolate(progress, [0, 1], [100, 0]);
  
  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#0f0f23",
        justifyContent: "center",
        alignItems: "center",
        overflow: "hidden",
      }}
    >
      <div style={{ position: "relative", fontSize: 120, fontWeight: "bold" }}>
        {logoText.split("").map((char, i) => {
          const isTop = i % 2 === 0;
          
          return (
            <div
              key={i}
              style={{
                display: "inline-block",
                color,
                transform: `translateY(${isTop ? topY : bottomY}px)`,
              }}
            >
              {char}
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

// 效果 56: 引导关注 - 箭头指向
export const CTAArrow: React.FC = () => {
  const frame = useCurrentFrame();
  const bounce = Math.abs(Math.sin(frame * 0.1)) * 20;
  
  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#1a1a2e",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 60, color: "#fff", marginBottom: 20 }}>
          关注我们
        </div>
        <div
          style={{
            fontSize: 80,
            transform: `translateY(${bounce}px)`,
          }}
        >
          ⬆️
        </div>
      </div>
    </AbsoluteFill>
  );
};

// 效果 57: 引导关注 - 点赞动画
export const CTALike: React.FC = () => {
  const frame = useCurrentFrame();
  const scale = 1 + Math.abs(Math.sin(frame * 0.15)) * 0.3;
  const hearts = [0, 1, 2].map((i) => {
    const delay = i * 10;
    const currentFrame = Math.max(0, frame - delay);
    const y = interpolate(currentFrame, [0, 60], [0, -200], {
      extrapolateRight: "clamp",
    });
    const opacity = interpolate(currentFrame, [0, 60], [1, 0], {
      extrapolateRight: "clamp",
    });
    
    return (
      <div
        key={i}
        style={{
          position: "absolute",
          fontSize: 60,
          left: `calc(50% + ${(i - 1) * 60}px)`,
          top: `calc(50% + ${y}px)`,
          opacity,
          transform: `translateX(-50%)`,
        }}
      >
        ❤️
      </div>
    );
  });
  
  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#0f0f23",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div
        style={{
          fontSize: 120,
          transform: `scale(${scale})`,
        }}
      >
        👍
      </div>
      {hearts}
    </AbsoluteFill>
  );
};

// 效果 58: 引导关注 - 脉冲圆圈
export const CTAPulse: React.FC = () => {
  const frame = useCurrentFrame();
  
  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#1a1a2e",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div
        style={{
          width: 200,
          height: 200,
          borderRadius: "50%",
          backgroundColor: "#ff6b6b",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          fontSize: 40,
          fontWeight: "bold",
          color: "white",
        }}
      >
        订阅
      </div>
      {[0, 1, 2].map((i) => {
        const delay = i * 15;
        const currentFrame = Math.max(0, frame - delay);
        const scale = interpolate(currentFrame, [0, 60], [1, 3], {
          extrapolateRight: "clamp",
        });
        const opacity = interpolate(currentFrame, [0, 60], [0.8, 0], {
          extrapolateRight: "clamp",
        });
        
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              width: 200,
              height: 200,
              borderRadius: "50%",
              border: "3px solid #ff6b6b",
              transform: `scale(${scale})`,
              opacity,
            }}
          />
        );
      })}
    </AbsoluteFill>
  );
};

// 效果 59: 引导关注 - 闪烁星星
export const CTASparkle: React.FC = () => {
  const frame = useCurrentFrame();
  const rotation = frame * 3;
  
  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#0f0f23",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div
        style={{
          fontSize: 100,
          transform: `rotate(${rotation}deg)`,
        }}
      >
        ✨
      </div>
      <div
        style={{
          position: "absolute",
          marginTop: 150,
          fontSize: 60,
          color: "white",
          fontWeight: "bold",
        }}
      >
        关注有惊喜
      </div>
    </AbsoluteFill>
  );
};

// 效果 60: 引导关注 - 进度条
export const CTAProgress: React.FC = () => {
  const frame = useCurrentFrame();
  const progress = interpolate(frame, [0, 120], [0, 100], {
    extrapolateRight: "clamp",
  });
  
  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#1a1a2e",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 60, color: "white", marginBottom: 40 }}>
          关注解锁更多精彩
        </div>
        <div
          style={{
            width: 400,
            height: 20,
            backgroundColor: "rgba(255, 255, 255, 0.2)",
            borderRadius: 10,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${progress}%`,
              height: "100%",
              background: "linear-gradient(90deg, #4ecdc4, #ff6b6b)",
              borderRadius: 10,
            }}
          />
        </div>
        <div style={{ marginTop: 20, fontSize: 40, color: "#4ecdc4" }}>
          {Math.floor(progress)}%
        </div>
      </div>
    </AbsoluteFill>
  );
};
